import {ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {fromEvent} from 'rxjs';
import {distinctUntilChanged, pairwise, switchMap, takeUntil} from 'rxjs/operators';
import {CanvasService} from '../../../core/services/canvas.service';
import {DeviceDetectorService} from 'ngx-device-detector';
import SignaturePad from 'signature_pad';

@Component({
  selector: 'app-canvas',
  templateUrl: './canvas.component.html',
  styleUrls: ['./canvas.component.scss']
})
export class CanvasComponent implements OnInit, OnChanges {


  @Input() public canvasWidth = 660;
  @Input() public canvasHeight = 300;
  @Input() public isConfigSignDefault = false;
  @Input() public isSignflowDocument = false;
  // ảnh chữ ký đã lưu (dataURL) — khôi phục vào ô vẽ khi mở lại màn (PYC-84222 task 3)
  @Input() public restoredImage: string = null;
  @Output() applySignatureImage = new EventEmitter();
  // bắn khi user bấm "Vẽ lại" (xóa trắng ô vẽ) — cha cần biết để vô hiệu hóa ảnh vẽ đang chờ lưu (PYC-84222)
  @Output() canvasCleared = new EventEmitter<void>();
  public disableBtnDeleteCanvas = true;
  public disableBtnApplyCanvas = true;

  // @Output() onGetCanvasImage = new EventEmitter<File>();

  @ViewChild('myCanvas', {static: true}) canvas: ElementRef;

  private cx: CanvasRenderingContext2D;
  currentColour = '#0000ff';
  brushSize = 5;
  deviceInfo = null;
  signaturePad: any;

  // private drawCanvas = document.querySelector('drawCanvas') as HTMLCanvasElement;
  // public signaturePad = new SignaturePad(this.drawCanvas, {
  //   minWidth: this.canvasWidth,
  //   penColor: '#0000ff'
  // });

  constructor(
    private canvasService: CanvasService,
    private cd: ChangeDetectorRef,
    public deviceService: DeviceDetectorService) {
    this.deviceInfo = this.deviceService.getDeviceInfo();
  }

  ngOnInit() {
    this.canvasService.isSelectSignDraw.pipe(distinctUntilChanged()).subscribe(result => {
      // init idempotent: pad đã tạo (tự init ở dưới hoặc lần select trước) thì không tạo lại,
      // tránh nhân đôi listener trên cùng canvas ở các màn tái chọn tab
      if (result === true && !this.signaturePad) {
        this.configCanvas();
      }
    });

    this.canvasService.isDisableBtnDeleteCanvas.subscribe(result => {
      this.disableBtnDeleteCanvas = result;
    });

    this.canvasService.isApplyDrawSignature.subscribe(result => {
      this.disableBtnApplyCanvas = result;
    });

    // chưa có subscription nào bắn event (component chỉ tạo 1 lần) -> tự init luôn
    if (!this.signaturePad) {
      this.configCanvas();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // có ảnh đã lưu -> vẽ thẳng vào ô vẽ (màn cấu hình ảnh ký mặc định + màn ký)
    if (changes.restoredImage && this.restoredImage) {
      this.restoreImage();
    }
  }

  /** vẽ ảnh đã lưu lại vào canvas; pad coi như có nội dung (bật nút Áp dụng) */
  private restoreImage() {
    if (!this.signaturePad) {
      return;
    }
    // ảnh vẽ lưu xuống BE luôn được export gấp 4 lần khung vẽ ở màn cấu hình,
    // nên khôi phục ở màn nào cũng phải chia lại 1/4 thì nét bút mới khớp nét vẽ mới
    // (fromDataURL mặc định stretch full khung -> nét to gấp 4, vẽ đè bị lệch)
    const image = new Image();
    image.onload = () => {
      if (!this.signaturePad) {
        return;
      }
      let drawWidth = image.naturalWidth / 4;
      let drawHeight = image.naturalHeight / 4;
      // chặn tràn khung, giữ tỉ lệ (phòng ảnh từ nguồn ngoài không phải export 4x)
      const fitScale = Math.min(1, this.canvasWidth / drawWidth, this.canvasHeight / drawHeight);
      drawWidth *= fitScale;
      drawHeight *= fitScale;
      this.signaturePad.fromDataURL(this.restoredImage, {
        ratio: 1,
        width: drawWidth,
        height: drawHeight
      }).then(() => {
        this.canvasService.setIsDisableBtnDeleteCanvas(false);
        this.canvasService.setIsApplyDrawSignature(true);
        this.disableBtnDeleteCanvas = false;
        this.disableBtnApplyCanvas = false;
      });
    };
    image.src = this.restoredImage;
  }


  configCanvas() {
    if (!this.canvas) {
      return;
    }
    const canvasEl: HTMLCanvasElement = this.canvas.nativeElement;
    this.cx = canvasEl.getContext('2d');
    // canvasEl.width = this.canvasWidth;
    // canvasEl.height = this.canvasHeight;
    // this.cx.lineWidth = 5;
    // this.cx.lineCap = 'round';
    // this.cx.strokeStyle = '#000';

    // const drawCanvas = document.querySelector('drawCanvas') as HTMLCanvasElement;
    this.signaturePad = new SignaturePad(canvasEl, {
      minWidth: 2,
      maxWidth: 2,
      penColor: '#0000ff',
      dotSize: 0.5,
    });

    this.signaturePad.addEventListener('beginStroke', () => {
      this.canvasService.setIsDisableBtnDeleteCanvas(false);
      // nét vẽ mới => nội dung canvas chưa được Áp dụng
      this.canvasService.setIsApplyDrawSignature(false);
    }, {once: false});

    // ảnh khôi phục có thể đến trước lúc pad được tạo (tab vẽ render lazy) -> vẽ bù ngay
    if (this.restoredImage) {
      this.restoreImage();
    }

    // this.captureEvents(canvasEl);
  }

  private captureEvents(canvasEl: HTMLCanvasElement) {
    // this will capture all mousedown events from the canvas element
    fromEvent(canvasEl, 'mousedown')
      .pipe(
        switchMap((e) => {
          // after a mouse down, we'll record all mouse moves
          return fromEvent(canvasEl, 'mousemove')
            .pipe(
              // we'll stop (and unsubscribe) once the user releases the mouse
              // this will trigger a 'mouseup' event
              takeUntil(fromEvent(canvasEl, 'mouseup')),
              // we'll also stop (and unsubscribe) once the mouse leaves the canvas (mouseleave event)
              takeUntil(fromEvent(canvasEl, 'mouseleave')),
              // pairwise lets us get the previous value to draw a line from
              // the previous point to the current point
              pairwise()
            );
        })
      )
      .subscribe((res: [MouseEvent, MouseEvent]) => {
        const rect = canvasEl.getBoundingClientRect();

        // previous and current position with the offset
        const prevPos = {
          x: res[0].clientX - rect.left,
          y: res[0].clientY - rect.top
        };

        const currentPos = {
          x: res[1].clientX - rect.left,
          y: res[1].clientY - rect.top
        };

        // this method we'll implement soon to do the actual drawing
        this.drawOnCanvas(prevPos, currentPos);
      });

    // this will capture all touchstart events from the canvas element
    fromEvent(canvasEl, 'touchstart')
      .pipe(
        switchMap((e) => {
          // after a mouse down, we'll record all mouse moves
          return fromEvent(canvasEl, 'touchmove')
            .pipe(
              // we'll stop (and unsubscribe) once the user releases the mouse
              // this will trigger a 'mouseup' event
              takeUntil(fromEvent(canvasEl, 'touchend')),
              // we'll also stop (and unsubscribe) once the mouse leaves the canvas (mouseleave event)
              takeUntil(fromEvent(canvasEl, 'touchcancel')),
              // pairwise lets us get the previous value to draw a line from
              // the previous point to the current point
              pairwise()
            );
        })
      )
      .subscribe((res: [TouchEvent, TouchEvent]) => {
        const rect = canvasEl.getBoundingClientRect();

        // previous and current position with the offset
        const prevPos = {
          x: res[0].touches[0].clientX - rect.left,
          y: res[0].touches[0].clientY - rect.top
        };

        // res[0].preventDefault();
        // res[0].stopImmediatePropagation();

        const currentPos = {
          x: res[1].touches[0].clientX - rect.left,
          y: res[1].touches[0].clientY - rect.top
        };

        // res[1].preventDefault();
        // res[1].stopImmediatePropagation();

        // this method we'll implement soon to do the actual drawing
        this.drawOnCanvas(prevPos, currentPos);
      });
  }


  private drawOnCanvas(prevPos: { x: number, y: number }, currentPos: { x: number, y: number }) {
    this.canvasService.setIsDisableBtnDeleteCanvas(false);
    // this.disableBtnDeleteCanvas = false;
    if (!this.cx) {
      return;
    }
    this.cx.beginPath();
    if (prevPos) {
      this.cx.moveTo(prevPos.x, prevPos.y); // from
      this.cx.lineTo(currentPos.x, currentPos.y);
      this.cx.closePath();
      this.cx.strokeStyle = this.currentColour;
      this.cx.lineWidth = this.brushSize;
      this.cx.stroke();
    }
  }

  clearCanvas() {
    this.canvasService.setIsDisableBtnDeleteCanvas(true);
    this.canvasService.setIsApplyDrawSignature(false);
    if(this.cx) {
      this.cx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
    }
    this.canvasCleared.emit();
  }

  handleOk(): void {
    this.canvasService.setIsVisibleModalDrawSignature(false);
    this.onSaveCanvas();
  }

  onSaveCanvas(isConfigSignatureDefault?: boolean): void {
    if (!this.signaturePad || this.signaturePad.isEmpty()) {
      return;
    }
    this.canvasService.setIsApplyDrawSignature(true);
    const canvasEl: HTMLCanvasElement = this.canvas.nativeElement;
    const resizedCanvas = document.createElement('canvas');
    const resizedContext = resizedCanvas.getContext('2d');
    resizedCanvas.height = this.isConfigSignDefault ? this.canvasHeight * 4 : 200;
    resizedCanvas.width = this.isConfigSignDefault ? this.canvasWidth * 4 : 440;
    resizedContext.drawImage(canvasEl, 0, 0, this.isConfigSignDefault ? this.canvasWidth * 4 : 440, this.isConfigSignDefault ? this.canvasHeight * 4 : 200);

    const imageUrl = resizedCanvas.toDataURL();
    if (isConfigSignatureDefault) {
      this.applySignatureImage.emit(resizedCanvas.toDataURL());
    }
    this.canvasService.setCanvasImageUrl(imageUrl);
    if(this.isSignflowDocument) {
      this.canvasService.setCanvasSignatureImageUrl(imageUrl);
    }
    // const imageUrl = canvasEl.toDataURL();
    // this.imageUrlPreview = imageUrl;
    fetch(imageUrl)
      .then(r => r.blob())
      .then(blobFile => {
        const fileUpload = new File([blobFile], 'fileNameGoesHere', {type: 'image/png'});
        this.canvasService.setCanvasImage(fileUpload);
        if(this.isSignflowDocument) {
          this.canvasService.setCanvasSignatureImage(fileUpload);
        }
      });
  }

  onSaveCanvasWithName(name: string): void {
    if (!this.signaturePad || this.signaturePad.isEmpty()) {
      return;
    }
    const canvasEl: HTMLCanvasElement = this.canvas.nativeElement;
    const resizedCanvas = document.createElement('canvas');
    const resizedContext = resizedCanvas.getContext('2d');
    resizedCanvas.height = 200;
    resizedCanvas.width = 440;
    resizedContext.drawImage(canvasEl, 0, 0, 440, 200);

    const imageUrl = resizedCanvas.toDataURL();
    this.canvasService.setCanvasImageUrl(imageUrl);
    // const imageUrl = canvasEl.toDataURL();
    // this.imageUrlPreview = imageUrl;
    fetch(imageUrl)
      .then(r => r.blob())
      .then(blobFile => {
        const fileUpload = new File([blobFile], name, {type: 'image/png'});
        this.canvasService.setCanvasImage(fileUpload);
      });
  }

  changeColorDrawCanvas(data: string) {
    this.signaturePad.penColor = data;
    this.cd.detectChanges();
  }
}
